import { useCallback, useEffect, useRef, useState } from "react";
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import { useAccessToken } from "@/auth/useAccessToken";

const hubBase = import.meta.env.VITE_CONNECT_BFF_BASE_URL ?? "http://localhost:5104";

// Names must match MeetingsHubEvents on the server.
export const MeetingsEvents = {
    UserJoined: "meeting.user.joined",
    UserLeft: "meeting.user.left",
    Participants: "meeting.participants",
    ReceiveOffer: "meeting.rtc.offer",
    ReceiveAnswer: "meeting.rtc.answer",
    ReceiveIceCandidate: "meeting.rtc.ice",
    MediaToggled: "meeting.media.toggled",
    MeetingEnded: "meeting.ended"
} as const;

type EventMap = Partial<Record<(typeof MeetingsEvents)[keyof typeof MeetingsEvents], (payload: unknown) => void>>;

export interface UseMeetingsHub {
    state: HubConnectionState;
    connectionId: string | null;
    joinMeeting: (meetingId: string) => Promise<void>;
    leaveMeeting: (meetingId: string) => Promise<void>;
    sendOffer: (targetConnectionId: string, offer: RTCSessionDescriptionInit) => Promise<void>;
    sendAnswer: (targetConnectionId: string, answer: RTCSessionDescriptionInit) => Promise<void>;
    sendIceCandidate: (targetConnectionId: string, candidate: RTCIceCandidateInit) => Promise<void>;
    toggleMedia: (mediaType: "audio" | "video", enabled: boolean) => Promise<void>;
    endMeeting: () => Promise<void>;
}

/**
 * Mail-web's wrapper for the Connect BFF's `/hubs/meetings`. Same shape
 * as connect-web's hook (lives there too) — duplicated rather than
 * extracted because mail-web only needs a slimmed-down event surface
 * and the `useAccessToken` import is local.
 */
export function useMeetingsHub(events: EventMap): UseMeetingsHub {
    const token = useAccessToken();
    const [state, setState] = useState<HubConnectionState>(HubConnectionState.Disconnected);
    const [connectionId, setConnectionId] = useState<string | null>(null);
    const connectionRef = useRef<HubConnection | null>(null);
    const handlersRef = useRef(events);
    handlersRef.current = events;

    useEffect(() => {
        if (!token) return;
        const hub = new HubConnectionBuilder()
            .withUrl(`${hubBase}/hubs/meetings`, { accessTokenFactory: () => token })
            .withAutomaticReconnect()
            .configureLogging(LogLevel.Warning)
            .build();

        for (const name of Object.values(MeetingsEvents)) {
            hub.on(name, (payload: unknown) => handlersRef.current[name]?.(payload));
        }

        const syncState = () => {
            setState(hub.state);
            setConnectionId(hub.connectionId ?? null);
        };
        hub.onclose(syncState);
        hub.onreconnecting(syncState);
        hub.onreconnected(syncState);

        connectionRef.current = hub;
        setState(HubConnectionState.Connecting);
        hub.start().then(syncState).catch(() => setState(HubConnectionState.Disconnected));

        return () => {
            hub.stop().catch(() => { /* ignore */ });
            connectionRef.current = null;
        };
    }, [token]);

    const invoke = useCallback(async (name: string, ...args: unknown[]) => {
        const hub = connectionRef.current;
        if (!hub || hub.state !== HubConnectionState.Connected) return;
        try { await hub.invoke(name, ...args); }
        catch { /* surface is out of band */ }
    }, []);

    return {
        state,
        connectionId,
        joinMeeting: (meetingId) => invoke("JoinMeeting", meetingId),
        leaveMeeting: (meetingId) => invoke("LeaveMeeting", meetingId),
        sendOffer: (target, offer) => invoke("SendOffer", target, offer),
        sendAnswer: (target, answer) => invoke("SendAnswer", target, answer),
        sendIceCandidate: (target, candidate) => invoke("SendIceCandidate", target, candidate),
        toggleMedia: (mediaType, enabled) => invoke("ToggleMedia", mediaType, enabled),
        endMeeting: () => invoke("EndMeeting")
    };
}
